import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { elements, actions } from '@ecosystem/newron-design-system';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

type Props = {
  term?: string;
  placeholder?: string;
  onChange: (term?: string) => void;
  disabled?: boolean;
};
function SearchInput({ term, placeholder, onChange, disabled }: Props) {
  const { isMobile } = useMobile();

  const [value, setValue] = useState<string>(term || '');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isInputHidden, setIsInputHidden] = useState<boolean>(isMobile);

  const timer = useRef<ReturnType<typeof setTimeout>>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    clearTimeout(timer.current);
    const newValue = event.target.value;
    setValue(newValue);

    timer.current = setTimeout(() => {
      onChange(newValue.length === 0 ? undefined : newValue);
    }, 150);
  };

  const handleClear = () => {
    setValue('');
    onChange();

    if (isMobile) {
      inputRef.current?.focus();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleMobileBlur = (e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setIsFocused(false);
      setIsInputHidden(true);
    }
  };

  const handlerMobileClick = () => {
    setIsInputHidden(false);
  };

  const { Icon } = elements;
  const { IconButton } = actions;
  const { Search, Close } = Icon.icons;

  const [portalEl, setPortalEl] = useState<Element | null>(null);

  useEffect(() => {
    if (!isMobile) {
      setPortalEl(null);
      return;
    }

    const target = document.querySelector('.mobile-search-portal');
    setPortalEl(target);
  }, [isMobile]);

  const searchBar = () => {
    return (
      <div
        ref={containerRef}
        className={classNames('bg-white rounded h-10 border border-default flex items-center px-2', {
          'shadow-focus border-stroke-dark': isFocused,
        })}
        data-testid="search-input"
        data-test-id="search-input"
      >
        <div className="">
          <Icon>
            <Search />
          </Icon>
        </div>
        <input
          ref={inputRef}
          disabled={disabled}
          type="text"
          placeholder={placeholder}
          className="TextInput pl-2"
          style={{ outline: 0 }}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={isMobile ? handleMobileBlur : handleBlur}
          autoFocus
        />
        <div className={`${term?.length ? 'visible' : 'hidden'}`}>
          <IconButton onClick={handleClear}>
            <Close />
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <>
      {isMobile && (
        <IconButton onClick={handlerMobileClick}>
          <Search />
        </IconButton>
      )}
      {!isMobile && searchBar()}
      {isMobile && !isInputHidden && portalEl && createPortal(searchBar(), portalEl)}
    </>
  );
}

export { SearchInput };
